import {
  ChangeDetectionStrategy, Component, ElementRef, input, linkedSignal, output, viewChildren
} from '@angular/core';
import {TabOptionModel} from './models/tab-option-model';

/**
 * The app's tab strip: a row of equally wide segments with the active one underlined in the accent colour
 * (`docs/articles/frontend/style-guide.md` → "active tab underline"). Used by the analytics charts to pick a
 * time range / breakdown, and by the register-onboarding page to switch between the QR and the credentials
 * tab.
 *
 * ### One strip at every width
 *
 * It used to swap to a native `<select>` below `sm`. A native select is never allowed here (it leaks a field's
 * data key through type-ahead and is unstylable), and the strip itself reflows fine: segments are `flex-1`
 * with tighter padding on phones, so up to four short labels share a 360px row and longer ones wrap inside
 * their segment rather than overflowing the page.
 *
 * ### It is a real `tablist`
 *
 * Segments are `<button role="tab">` with `aria-selected`, roving `tabindex` and arrow-key movement per the
 * WAI-ARIA tabs pattern — the previous `<a>` without an `href` was neither focusable nor announced as a tab.
 * Each button carries `id="tab-<option id>"`, so a panel can point back at it with `aria-labelledby`.
 *
 * The selection is a `linkedSignal` off the `selectedOption` input: a click moves it locally and emits, and a
 * parent that passes a new `selectedOption` resets it — the same contract the old setter + field pair had.
 */
@Component({
  selector: 'app-tab-selection',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tab-selection.component.html'
})
export class TabSelectionComponent {
  /** The segments, in display order. Nothing renders while this is empty. */
  readonly options = input<TabOptionModel[]>([]);

  /** The segment to show as active. Undefined leaves the strip rendered with no active segment. */
  readonly selectedOption = input<TabOptionModel | undefined>(undefined);

  /** Fires with the segment the user chose — on click and on arrow-key movement. */
  readonly optionSelected = output<TabOptionModel>();

  protected readonly selected = linkedSignal(() => this.selectedOption());

  private readonly tabs = viewChildren<ElementRef<HTMLButtonElement>>('tab');

  protected isActive(option: TabOptionModel): boolean {
    return this.selected()?.id === option.id;
  }

  /** The button id a tab panel refers to through `aria-labelledby`. */
  protected tabId(option: TabOptionModel): string {
    return `tab-${option.id}`;
  }

  protected select(option: TabOptionModel): void {
    this.selected.set(option);
    this.optionSelected.emit(option);
  }

  /** Arrow keys move and select (automatic activation); Home/End jump to the ends. */
  protected onKeydown(event: KeyboardEvent, index: number): void {
    const options = this.options();
    const last = options.length - 1;
    let target: number;

    switch (event.key) {
      case 'ArrowRight': target = index === last ? 0 : index + 1; break;
      case 'ArrowLeft': target = index === 0 ? last : index - 1; break;
      case 'Home': target = 0; break;
      case 'End': target = last; break;
      default: return;
    }

    event.preventDefault();
    this.select(options[target]);
    this.tabs()[target]?.nativeElement.focus();
  }
}
